/**
 * AuthGate Component
 * Protects routes that require authentication
 */

import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import useAuthStore from '@/store/authStore';

interface AuthGateProps {
  children: React.ReactNode;
}

export default function AuthGate({ children }: AuthGateProps) {
  const location = useLocation();
  const { status, token, refreshProfile } = useAuthStore();

  // Refresh user profile on mount if we have a token
  useEffect(() => {
    if (token && status === 'authenticated') {
      refreshProfile().catch((error) => {
        console.error('Failed to refresh profile:', error);
      });
    }
  }, [token, status, refreshProfile]);

  // Still loading (only show loading during active authentication)
  if (status === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated - redirect to login
  // Treat 'idle' without token as unauthenticated
  if (status === 'unauthenticated' || status === 'requires2fa' || (status === 'idle' && !token)) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Authenticated - render children
  return <>{children}</>;
}
